import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { notify } from "src/components/base/toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import TablePagination from "src/components/table-pagination";
import SETTINGS_CONTEXT from "src/context/internal/settings";
import { Strong, Text } from "src/components/base/text";
import { Badge } from "src/components/base/badge";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import {
    CheckIcon,
    ChevronDownIcon,
    ClipboardDocumentIcon,
    EllipsisVerticalIcon,
    NoSymbolIcon,
    TrashIcon,
} from "@heroicons/react/20/solid";
import { Button, PrimaryButton } from "src/components/base/button";
import { Dialog, DialogActions, DialogTitle } from "src/components/base/dialog";
import { useForm } from "@tanstack/react-form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import Form from "src/components/base/form";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import WS from "src/api/ws";
import { UUID } from "src/api/api_common";
import { TagBadge } from "src/components/badges/tag";
import { StatusBadge } from "src/components/badges/status";

/**
 * The properties for {@link ApprovedExitNodesOverview}
 */
export type ApprovedExitNodesOverviewProps = {};

/**
 * Filter options
 */
type Filter = "all" | "disabled" | "enabled" | "advertises";

const LIMIT = 25;

/**
 * Overview for all approved exit nodes
 */
export default function ApprovedExitNodesOverview(props: ApprovedExitNodesOverviewProps) {
    const [t] = useTranslation("admin-exit-nodes");
    const [tg] = useTranslation();
    const navigate = useNavigate();

    const [openDelete, setOpenDelete] = React.useState<UUID | undefined>();
    const [openDisable, setOpenDisable] = React.useState<UUID | undefined>();
    const [openEnable, setOpenEnable] = React.useState<UUID | undefined>();

    const { page, search, filter } = Route.useSearch();
    const router = useRouter();

    const settingsContext = React.useContext(SETTINGS_CONTEXT);

    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.ExitNodesChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <div className={"flex flex-col gap-6"}>
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) => {
                                    await navigate({
                                        to: "/a/exitnodes/approved",
                                        search: {
                                            page: 1,
                                            search: fieldApi.state.value,
                                            filter,
                                        },
                                    });
                                },
                                onChangeAsyncDebounceMs: 500,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Input
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>

                <Fieldset className={"max-w-56"}>
                    <FieldGroup>
                        <Field>
                            <Label className={"w-full"}>{t("label.show-all")}</Label>
                            <Listbox
                                value={filter}
                                onChange={async (x) => {
                                    await navigate({
                                        to: "/a/exitnodes/approved",
                                        search: {
                                            page: 1,
                                            filter: x,
                                            search,
                                        },
                                    });
                                }}
                            >
                                <ListboxOption value={"all"}>
                                    <ListboxLabel>{t("label.show-all")}</ListboxLabel>
                                </ListboxOption>
                                <ListboxOption value={"enabled"}>
                                    <CheckIcon />
                                    <ListboxLabel>{t("label.show-enabled-only")}</ListboxLabel>
                                </ListboxOption>
                                <ListboxOption value={"disabled"}>
                                    <NoSymbolIcon />
                                    <ListboxLabel>{t("label.show-disabled-only")}</ListboxLabel>
                                </ListboxOption>
                                <ListboxOption value={"advertises"}>
                                    <ListboxLabel>{t("label.show-not-advertises-only")}</ListboxLabel>
                                </ListboxOption>
                            </Listbox>
                        </Field>
                    </FieldGroup>
                </Fieldset>
            </Form>
            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.device-name")}</TableHeader>
                        <TableHeader>{t("label.last-seen")}</TableHeader>
                        <TableHeader>{t("label.addresses")}</TableHeader>
                        <TableHeader className={"max-lg:hidden"}>{t("label.first-login")}</TableHeader>
                        <TableHeader className={"w-0"}>{t("label.status")}</TableHeader>
                        <TableHeader className={"w-0"}>
                            <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map(({ device, enabled, advertise, uuid }) => (
                        <TableRow href={"/a/devices/$deviceId/general"} params={{ deviceId: device.uuid }} key={uuid}>
                            <TableCell>
                                <div className={"flex flex-col gap-1"}>
                                    <div className={"flex gap-2"}>
                                        <Strong>{device.dnsname}</Strong>
                                        {!advertise && <Badge color={"teal"}>{t("label.not-advertises")}</Badge>}
                                    </div>
                                    <div className={"flex flex-wrap gap-1"}>
                                        {device.tags?.map((tag) => (
                                            <TagBadge key={tag.uuid} tag={tag} />
                                        ))}
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <StatusBadge compact online={device.online} lastSeen={device.last_seen} />
                            </TableCell>
                            <TableCell>
                                <div className={"flex items-center gap-1"}>
                                    <span>{device.ipv4}</span>
                                    <Dropdown>
                                        <DropdownButton plain={true}>
                                            <ChevronDownIcon />
                                        </DropdownButton>
                                        <DropdownMenu anchor={"bottom end"}>
                                            {[
                                                device.ipv4,
                                                device.ipv6,
                                                device.dnsname,
                                                ...settingsContext.settings.headscale_domains.map(
                                                    (domain) => `${device.dnsname}.${domain}`,
                                                ),
                                            ].map(
                                                (address) =>
                                                    address && (
                                                        <DropdownItem
                                                            key={address}
                                                            onClick={async () => {
                                                                await navigator.clipboard.writeText(address);
                                                                notify.success(tg("toast.copied-to-clipboard"));
                                                            }}
                                                        >
                                                            <ClipboardDocumentIcon />
                                                            <DropdownLabel>{address}</DropdownLabel>
                                                        </DropdownItem>
                                                    ),
                                            )}
                                        </DropdownMenu>
                                    </Dropdown>
                                </div>
                            </TableCell>

                            <TableCell className={"max-lg:hidden"}>
                                {new Date(device.created_at).toLocaleDateString("de-de")}
                            </TableCell>
                            <TableCell className={"max-lg:hidden"}>
                                {enabled ? (
                                    <div className={"flex items-center gap-1"}>
                                        <CheckIcon className={"size-4"} /> <Text>{t("label.enabled")}</Text>
                                    </div>
                                ) : (
                                    <div className={"flex items-center gap-1"}>
                                        <NoSymbolIcon className={"size-4"} /> <Text>{t("label.disabled")}</Text>
                                    </div>
                                )}
                            </TableCell>
                            <TableCell>
                                <Dropdown>
                                    <DropdownButton plain={true}>
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownSection>
                                            <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>

                                            <DropdownItem disabled={enabled} onClick={() => setOpenEnable(uuid)}>
                                                <CheckIcon />
                                                <DropdownLabel>{t("button.enable")}</DropdownLabel>
                                            </DropdownItem>

                                            <DropdownItem disabled={!enabled} onClick={() => setOpenDisable(uuid)}>
                                                <NoSymbolIcon />
                                                <DropdownLabel>{t("button.disable")}</DropdownLabel>
                                            </DropdownItem>

                                            <DropdownItem onClick={() => setOpenDelete(uuid)}>
                                                <TrashIcon />
                                                <DropdownLabel>{t("button.delete")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                    </DropdownMenu>
                                </Dropdown>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/a/exitnodes/approved"}
                getSearchParams={(newPage: number) => ({ page: newPage })}
            />

            {openEnable && (
                <Dialog open={true} onClose={() => setOpenEnable(undefined)}>
                    <DialogTitle>{t("heading.enable-exit-node")}</DialogTitle>

                    <DialogActions>
                        <Button plain={true} onClick={() => setOpenEnable(undefined)}>
                            {tg("button.cancel")}
                        </Button>
                        <PrimaryButton
                            onClick={async () => {
                                if (openEnable === undefined) return;
                                await ApiInternal.exitNode.enable(openEnable);
                                setOpenEnable(undefined);
                                await router.invalidate();
                            }}
                        >
                            {t("button.enable")}
                        </PrimaryButton>
                    </DialogActions>
                </Dialog>
            )}

            {openDisable && (
                <Dialog open={true} onClose={() => setOpenDisable(undefined)}>
                    <DialogTitle>{t("heading.disable-exit-node")}</DialogTitle>

                    <DialogActions>
                        <Button plain={true} onClick={() => setOpenDisable(undefined)}>
                            {tg("button.cancel")}
                        </Button>
                        <Button
                            color={"red"}
                            onClick={async () => {
                                if (openDisable === undefined) return;
                                await ApiInternal.exitNode.disable(openDisable);
                                setOpenDisable(undefined);
                                await router.invalidate();
                            }}
                        >
                            {t("button.disable")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {openDelete && (
                <Dialog open={true} onClose={() => setOpenDelete(undefined)}>
                    <DialogTitle>{t("heading.delete-exit-node")}</DialogTitle>

                    <DialogActions>
                        <Button plain={true} onClick={() => setOpenDelete(undefined)}>
                            {tg("button.cancel")}
                        </Button>
                        <Button
                            color={"red"}
                            onClick={async () => {
                                if (openDelete) await ApiInternal.exitNode.delete(openDelete);
                                setOpenDelete(undefined);
                            }}
                        >
                            {t("button.delete")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </div>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type ApprovedExitNodesOverviewSearch = {
    /** The current page number we're on */
    page: number;
    /** The search term that should be used */
    search?: string;
    /** The type of filter to use */
    filter?: Filter;
};

export const Route = createFileRoute("/_admin/a/exitnodes/_exitnodes/approved")({
    component: ApprovedExitNodesOverview,
    validateSearch: (search: Record<string, unknown>): ApprovedExitNodesOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return {
            page: page <= 0 ? 1 : page,
            search: (search.search as string) || "",
            filter: (search.filter as Filter) || "all",
        };
    },
    loaderDeps: ({ search: { page, search, filter } }) => ({ page, search, filter }),
    loader: async ({ deps }) =>
        await ApiInternal.exitNode.getAll({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
            filter_name: deps.search,
            filter_is_disabled:
                deps.filter === "all" || deps.filter === "advertises" ? undefined : deps.filter === "enabled",
            filter_device_advertises: deps.filter === "advertises" ? false : undefined,
        }),
});
